import React from 'react';
import {useDispatch} from 'react-redux';
import {HiOutlineExclamationCircle, HiOutlineSparkles} from 'react-icons/hi';
import {
  resetAllChatsStreaming,
  setIsNewChat,
  setLastActiveChatId,
  setShowBigChatbox,
  setShowChatbox,
  setShowNormalChatbox
} from '../../redux/slices/chat';
import CustomButton from "../../components/common/CustomButton";

interface ProjectEmptyStateModalProps {
  showModal: boolean;
  entityType?: string;
  simFlag: boolean;
  hasSimulationTasks: boolean;
  onToggleSim?: () => void;
  onMerge?: () => void;
}

const ProjectEmptyStateModal: React.FC<ProjectEmptyStateModalProps> = ({
  showModal,
  entityType = "tasks",
  simFlag,
  hasSimulationTasks,
  onToggleSim,
  onMerge
}) => {
  const dispatch = useDispatch();

  const handleAskCadence = () => {
    // Open the chatbot in shrinked mode without any prefill
    dispatch(resetAllChatsStreaming());
    dispatch(setIsNewChat(true));
    dispatch(setLastActiveChatId(null));
    dispatch(setShowChatbox(true));
    dispatch(setShowNormalChatbox(true));
    dispatch(setShowBigChatbox(false));
  };

  if (!showModal) return null;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      {/* Empty State Modal */}
      <div className="relative bg-secondary-light dark:bg-secondary-dark rounded-xl shadow-2xl max-w-lg w-full mx-4 border border-gray-200 dark:border-gray-700 overflow-hidden">

        {/* Header */}
        <div className="pt-5 pb-3 px-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <HiOutlineExclamationCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {!simFlag && hasSimulationTasks
                ? `No ${entityType} in Live Mode`
                : `No ${entityType} Found`
              }
            </h3>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-5">
          {!simFlag && hasSimulationTasks ? (
            // Live mode with simulation tasks - suggest switching to simulation
            <div className="bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-gray-700 rounded-lg p-3.5 mb-4">
              <div className="flex items-start space-x-2.5">
                <HiOutlineSparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm text-start font-semibold text-gray-900 dark:text-gray-100 mb-1.5 leading-tight">
                    <span className='capitalize'>{entityType}</span> are available in simulation mode
                  </h4>
                  <p className="text-xs text-start text-gray-600 dark:text-gray-400 leading-relaxed">
                    Toggle to simulation mode to view and work with existing {entityType.toLowerCase()},
                    or let Cadence create a project plan that you can then merge to live.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // No simulation tasks or in simulation mode - suggest creating plan
            <div className="bg-light-surfaceSecondary dark:bg-secondary-dark border border-light-border-default dark:border-gray-700 rounded-lg p-3.5 mb-4">
              <div className="flex items-start space-x-2.5">
                <HiOutlineSparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm text-start font-semibold text-gray-900 dark:text-gray-100 mb-1.5 leading-tight">
                    Let Cadence help you get started
                  </h4>
                  <p className="text-xs text-start text-gray-600 dark:text-gray-400 leading-relaxed">
                    Our AI assistant can create a comprehensive project plan using your work unit's name and description,
                    breaking it down into manageable {entityType.toLowerCase()} with dependencies and timelines.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!simFlag && hasSimulationTasks ? (
            <div className="flex gap-2.5">
              <CustomButton
                onClick={onToggleSim}
                variant="primary"
                className="flex flex-1 items-center justify-center space-x-2 px-4 py-2.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                <span>View in Simulation</span>
              </CustomButton>
              <CustomButton
                onClick={onMerge}
                variant="primary"
                className="flex flex-1 items-center justify-center space-x-2 px-4 py-2.5 text-xs font-medium transition-colors"
              >
                <span>Merge to Live</span>
              </CustomButton>
            </div>
          ) : (
            <div className="flex w-full items-center justify-center">
              <CustomButton
                onClick={handleAskCadence}
                variant="primary"
                className="flex w-full items-center justify-center space-x-2 px-5 py-2.5 text-xs font-medium transition-colors"
              >
                <HiOutlineSparkles className="w-3.5 h-3.5" />
                <span>Ask Cadence to Create Plan</span>
              </CustomButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectEmptyStateModal;